import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query"
import {
  AiMessages,
  AiMessagesPaginate,
  AiMessageStatusEnum,
  AiRunKindFilterEnum,
  ChannelType
} from "__generated__/graphql"

import { matchesRunKind } from "./sub_components/aiMessage.utils"

type AiMessageListData = InfiniteData<AiMessagesPaginate>

function getConversationId(item: AiMessages): string | undefined {
  const raw = item.conversationId as unknown
  if (!raw) return undefined
  if (typeof raw === "object" && "_id" in (raw as Record<string, unknown>)) {
    return String((raw as { _id: unknown })._id)
  }
  return String(raw)
}

function getThreadKeyConversationId(key: QueryKey): string | undefined {
  const raw = key[2]
  if (!raw) return undefined
  if (typeof raw === "object" && "_id" in (raw as Record<string, unknown>)) {
    return String((raw as { _id: unknown })._id)
  }
  return String(raw)
}

/** Sidebar list key shape: ["ai-message-list", search, customerId, channelType, status, startDate, endDate, kind]. */
function matchesSidebarFilters(item: AiMessages, key: QueryKey): boolean {
  const [, search, customerId, channelType, status, startDate, , kind] =
    key as [
      string,
      string | undefined,
      string | undefined,
      ChannelType | undefined,
      AiMessageStatusEnum | undefined,
      Date | undefined,
      Date | undefined,
      AiRunKindFilterEnum | undefined
    ]

  if (customerId && item.customerId !== customerId) return false
  if (channelType && item.channelType !== channelType) return false
  if (status && item.status !== status) return false
  if (kind && !matchesRunKind(item, kind)) return false
  if (startDate && new Date(item.createdAt as Date) < new Date(startDate))
    return false
  // No upper-bound (endDate) check: the default filter freezes endDate to
  // "now" at page-load time, so a live event arriving afterwards would always
  // fail that check and get silently dropped from an otherwise-live list.
  if (search) {
    const q = search.toLowerCase()
    const haystack = `${item.question ?? ""} ${item.answer ?? ""}`.toLowerCase()
    if (!haystack.includes(q)) return false
  }
  return true
}

function findExistingIndex(
  data: AiMessageListData,
  predicate: (item: AiMessages) => boolean
): { pageIndex: number; docIndex: number } | null {
  for (let pageIndex = 0; pageIndex < data.pages.length; pageIndex++) {
    const docs = data.pages[pageIndex]?.docs ?? []
    const docIndex = docs.findIndex((d) => !!d && predicate(d))
    if (docIndex !== -1) return { pageIndex, docIndex }
  }
  return null
}

/**
 * Applies a `createdAiMessage` event to one mounted ["ai-message-list", ...] query.
 * Sidebar (grouped-by-conversation) queries: replace the conversation's existing row so
 * only the latest message per conversation stays visible. Thread (flat, per-conversation)
 * queries: append as a new row, scoped to the currently open conversation.
 */
export function applyCreatedAiMessage(
  queryClient: QueryClient,
  incoming: AiMessages
): void {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: ["ai-message-list"]
  })

  for (const query of queries) {
    const key = query.queryKey
    const isThreadQuery = key.length <= 3

    if (isThreadQuery) {
      const threadCustomerId = key[1] as string | undefined
      const threadConversationId = getThreadKeyConversationId(key)
      if (threadCustomerId && threadCustomerId !== incoming.customerId) {
        continue
      }
      if (
        threadConversationId &&
        threadConversationId !== getConversationId(incoming)
      ) {
        continue
      }
    } else if (!matchesSidebarFilters(incoming, key)) {
      continue
    }

    queryClient.setQueryData<AiMessageListData>(key, (old) => {
      if (!old?.pages?.length) return old

      const existing = findExistingIndex(old, (d) => d._id === incoming._id)
      if (existing) return old

      if (isThreadQuery) {
        const lastPageIndex = old.pages.length - 1
        const pages = old.pages.map((page, idx) =>
          idx === lastPageIndex
            ? {
                ...page,
                docs: [...(page.docs ?? []), incoming],
                totalDocs: (page.totalDocs ?? 0) + 1
              }
            : page
        )
        return { ...old, pages }
      }

      const sameConversation = getConversationId(incoming)
      const pages = old.pages.map((page, idx) => {
        const docs = (page.docs ?? []).filter(
          (d) =>
            !d || !sameConversation || getConversationId(d) !== sameConversation
        )
        return idx === 0
          ? {
              ...page,
              docs: [incoming, ...docs],
              totalDocs: (page.totalDocs ?? 0) + 1
            }
          : { ...page, docs }
      })
      return { ...old, pages }
    })
  }
}

/**
 * Applies an `updatedAiMessage` event (run finished: status/answer/error) to every
 * mounted ["ai-message-list", ...] query that currently holds this message, in place.
 */
export function applyUpdatedAiMessage(
  queryClient: QueryClient,
  incoming: AiMessages
): void {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: ["ai-message-list"]
  })

  for (const query of queries) {
    queryClient.setQueryData<AiMessageListData>(query.queryKey, (old) => {
      if (!old?.pages?.length) return old

      const location = findExistingIndex(old, (d) => d._id === incoming._id)
      if (!location) return old

      const pages = old.pages.map((page, pageIndex) => {
        if (pageIndex !== location.pageIndex) return page
        const docs = (page.docs ?? []).map((d, docIndex) =>
          docIndex === location.docIndex && d ? { ...d, ...incoming } : d
        )
        return { ...page, docs }
      })
      return { ...old, pages }
    })
  }
}
